import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, Tab, Box, Grid, Typography, Button, Divider, IconButton, Autocomplete, Tooltip, TextField, CircularProgress, Snackbar, Alert, Modal, Switch } from '@mui/material';
import ContentPackTable from "./ContentPackTable";
import ActiveContentPackTable from "./ActiveContentPackTable";
import MainCard from "../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import { getCardColor, getTextColor } from "../../utils/colorUtils";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { useGetTenancyByIdQuery } from "../../store/services/tenancy.service";
import {
    useGetContentPacksQuery,
    useGetContentPackAssignmentsQuery,
    useCreateContentPackAssignmentMutation,
    useUpdateContentPackAssignmentMutation,
    useDeleteContentPackAssignmentMutation
} from "../../store/services/university-service";
import { useUpdateTenancyMutation } from "../../store/services/tenancy.service";
import styled from "styled-components";
import ContentPackPreview from "./ContentPackPreview";
import LoadingSwitch from "../../components/LoadingSwitch";
import { ModalProvider } from '../../components/Modal/ModalContext';
import ContentPackStatusCard from '../../components/cards/ContentPackStatusCard';
import HelpIcon from "@mui/icons-material/HelpOutline";
import ConfirmationDialog from "../../utils/confirmationDialog";
import { useNavigate } from 'react-router-dom';
import BlockingModal from '../../components/Modal/BlockingModal';
import {
    useGetUniversityCourseSettingsQuery,
} from "../../store/services/university-service";





const ButtonContainer = styled.div`
    margin-top: 5px;
`;

const ContentPacks = () => {
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [localTenancyData, setLocalTenancyData] = useState();
    const [updateTenancy] = useUpdateTenancyMutation();
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewContentPackId, setPreviewContentPackId] = useState(false);
    const [isContentPackEnabled, setIsContentPackEnabled] = useState(false);
    const [isPackSettingLoading, setIsPackSettingLoading] = useState(true);
    const [isUpdatingContentPack, setIsUpdatingContentPack] = useState(false);
    const [coursePreviewOpen, setCoursePreviewOpen] = useState(false);

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Add new state for confirmation dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");
    const [dialogAction, setDialogAction] = useState(null);
    const [dialogCancelAction, setDialogCancelAction] = useState(null);

    // New state to store pending content pack change
    const [pendingContentPack, setPendingContentPack] = useState(null);

    const [hideLegacyPacks, setHideLegacyPacks] = useState(false);
    const [legacyBannerDismissed, setLegacyBannerDismissed] = useState(false);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });


    const { data: tenancyData, isLoading: tenancyLoading, error: tenancyError } = useGetTenancyByIdQuery({ tenancyId });
    const {
        data: universitySettings,
        error: universitySettingsError,
        isLoading: universitySettingsLoading,
        refetch: refetchUniversitySettings
    } = useGetUniversityCourseSettingsQuery({ tenancyId: tenancyId });

    const {
        data: contentPacks,
        isLoading: contentPacksLoading,
        error: contentPacksError,
        refetch: refetchContentPacks
    } = useGetContentPacksQuery({
        perPage: 100,
        page: 0,
        searchTerm: '',
        tenancyId: tenancyId
    });

    const {
        data: assignments,
        isLoading: assignmentsLoading,
        error: assignmentsError,
        refetch: refetchAssignments
    } = useGetContentPackAssignmentsQuery({ tenancyId });

    const [createAssignment, { isLoading: isCreatingAssignment }] = useCreateContentPackAssignmentMutation();
    const [updateAssignment, { isLoading: isUpdatingAssignment }] = useUpdateContentPackAssignmentMutation();
    const [deleteAssignment] = useDeleteContentPackAssignmentMutation();


    const legacyPacks = useMemo(() =>
        (contentPacks || []).filter(p => p.tenancyId != null && p.tenancyId !== 0), [contentPacks]);
    const hasLegacyPacks = legacyPacks.length > 0;

    const filteredContentPacks = useMemo(() => {
        if (!contentPacks) return [];
        if (hideLegacyPacks) return contentPacks.filter(p => p.tenancyId == null || p.tenancyId === 0);
        return contentPacks;
    }, [contentPacks, hideLegacyPacks]);

    const selectedContentPack = contentPacks?.find(pack => pack.internalId === tenancyData?.contentPackId) || null;

    const handleAssignConfirm = async (assignData) => {
        const selectedPack = contentPacks?.find(p => p.internalId === assignData.contentPackId);
        if (!selectedPack) return;

        try {
            await createAssignment({
                contentPackId: assignData.contentPackId,
                tenancyId: String(tenancyId),
                scope: assignData.grouping,
                scopeId: assignData.grouping === 'Tenancy' ? null : assignData.recipients?.map(r => r.name).join(','),
                refreshEnabled: assignData.annualRefresh
            }).unwrap();
            setSnackbarMessage("Content pack assigned successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            console.error("Error creating content pack assignment:", error);
            setSnackbarMessage("Error assigning content pack.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleEditAssignment = async (editData) => {
        try {
            const existingAssignment = assignments?.find(a => a.id === editData.id);
            if (!existingAssignment) return;

            await updateAssignment({
                id: editData.id,
                assignmentData: {
                    ...existingAssignment,
                    scope: editData.groupingType,
                    scopeId: editData.groupingType === 'Tenancy' ? null : editData.groupingValues?.map(v => v.name).join(','),
                    refreshEnabled: editData.annualRefresh
                }
            }).unwrap();
            setSnackbarMessage("Assignment updated successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            console.error("Error updating assignment:", error);
            setSnackbarMessage("Error updating assignment.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleDeleteAssignment = async (assignmentId) => {
        try {
            await deleteAssignment(assignmentId).unwrap();
            setSnackbarMessage("Assignment deleted successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            console.error("Error deleting assignment:", error);
            setSnackbarMessage("Error deleting assignment.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };



    const handleSave = (updatedData) => {
        setIsUpdatingContentPack(true);
        updateTenancy({ data: updatedData, tenancyId: updatedData.tenancyId })
            .unwrap()
            .then(() => {
                setSnackbarMessage("Content pack settings updated successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            })
            .catch((error) => {
                console.log("Error updating content pack settings:", error);
                setSnackbarMessage("Error updating content pack settings.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            })
            .finally(() => {
                setIsUpdatingContentPack(false);
            });
    };

    const handleContentPackChange = (event, newValue) => {
        if (newValue) {
            setPendingContentPack(newValue);
            setDialogTitle("Set content pack");
            setDialogContent(`Are you sure you want to set the active content pack to "${newValue?.contentPackName}"`);
            setDialogCancelAction(() => () => {
                setPendingContentPack(null);
                if (selectedContentPack) {
                    setLocalTenancyData({
                        ...tenancyData,
                        contentPackId: selectedContentPack.internalId
                    });
                } else {
                    setIsContentPackEnabled(false);
                }
            });
            setDialogAction(() => () => {
                const updatedData = {
                    ...tenancyData,
                    contentPackId: newValue.internalId
                };
                setLocalTenancyData(updatedData);
                handleSave(updatedData);
                setPendingContentPack(null);
            });
            setDialogOpen(true);
        }
    };

    const handleToggleEnabled = (checked) => {
        if (checked) {
            setIsContentPackEnabled(true);
        } else if (selectedContentPack) {
            setDialogTitle("Remove content pack");
            setDialogContent("Are you sure you want to remove the active content pack? This will stop content pack sends for all users");
            setDialogCancelAction(() => () => {
                setIsContentPackEnabled(true);
            });
            setDialogAction(() => () => {
                const updatedData = {
                    ...tenancyData,
                    contentPackId: null
                };
                setIsContentPackEnabled(false);
                setLocalTenancyData(updatedData);
                handleSave(updatedData);
            });
            setDialogOpen(true);
        } else {
            setIsContentPackEnabled(false);
        }
    };

    const handlePreview = (contentPackId) => {
        setPreviewContentPackId(contentPackId);
        setPreviewModalOpen(true);
    };

    const handleClosePreviewModal = () => {
        if (!coursePreviewOpen) {
            setPreviewModalOpen(false);
            setPreviewContentPackId(null);
        }
    };
    useEffect(() => {
        if (tenancyData) {
            setLocalTenancyData(tenancyData);
            setIsContentPackEnabled(!!tenancyData.contentPackId);
            setIsPackSettingLoading(false);
        }
    }, [tenancyData]);
    

    const isPacksDataLoading = (tenancyLoading || contentPacksLoading)

    const getContentPackCardStatus = () => {
        if (contentPacksError) return 'Error';
        if (isPacksDataLoading) return 'Loading';
        if (selectedContentPack) return 'On';
        return 'Off';
    }


    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <>
            {/* Subrail Navigation */}
            <Box sx={{ mx: 2, mb: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="Active" />
                    <Tab label="Packs" />
                </Tabs>
            </Box>

            {/* Old header section - commented out for now */}
            {/* <Grid
                container
                rowSpacing={4.5}
                columnSpacing={2.75}
                alignItems="center"
                sx={{
                    minWidth: isContentPackEnabled ? 900 : 550,
                    flexWrap: 'nowrap'
                }}
            >
                <Grid
                    item
                    xs={2}
                    sx={{
                        minWidth: 200,
                        flexShrink: 0,
                        mb:3
                    }}
                >
                    <ContentPackStatusCard status={getContentPackCardStatus()} />
                </Grid>
                <Grid
                    item
                    xs={isContentPackEnabled ? 8 : 4}
                    sx={{
                        minWidth: isContentPackEnabled ? 650 : 350,
                        flexShrink: 0,
                        mb: 3
                    }}
                >
                    <MainCard
                        content
                        boxShadow
                        border={false}
                        sx={{
                            bgcolor: getCardColor(lightMode),
                            display: "flex",
                            flexDirection: "column",
                            height: 50,
                            justifyContent: "center",

                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>

                            {(contentPacksLoading || isPackSettingLoading) && (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
                                    <Typography variant="h6">Loading content packs list...</Typography>
                                </Box>
                            )}

                            {contentPacksError && !contentPacksLoading && (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
                                    <Typography variant="h6">Error fetching current content pack.</Typography>
                                </Box>
                            )}

                            {!contentPacksLoading && !isPackSettingLoading && !contentPacksError && (
                                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        width: "100%",

                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Tooltip title="The selected content pack will be sent to all new users">
                                                <IconButton>
                                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Typography variant="h5" sx={{ mr: 2 }}>
                                                Select a content pack
                                                </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <LoadingSwitch
                                                checked={isContentPackEnabled}
                                                onChange={() => {
                                                    const newValue = !isContentPackEnabled;
                                                    handleToggleEnabled(newValue);
                                                }}
                                                disabled={contentPacksLoading || isPackSettingLoading}
                                                loading={isUpdatingContentPack}
                                                inputProps={{ 'aria-label': 'content pack toggle' }}
                                            />
                                            {isContentPackEnabled && (
                                                <Autocomplete
                                                    key={isContentPackEnabled ? 'enabled' : 'disabled'}
                                                    value={selectedContentPack}
                                                    onChange={handleContentPackChange}
                                                    options={contentPacks || []}
                                                    getOptionLabel={(option) => option?.contentPackName || ''}
                                                    isOptionEqualToValue={(option, value) =>
                                                        option?.internalId === value?.internalId
                                                    }
                                                    loading={contentPacksLoading}
                                                    sx={{ width: 300 }}
                                                    clearOnBlur={false}
                                                    onClose={() => setPendingContentPack(null)}
                                                    clearText="Remove content pack"
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Select a content pack"
                                                            size="small"
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                endAdornment: (
                                                                    <>
                                                                        {contentPacksLoading ? (
                                                                            <CircularProgress color="inherit" size={20} />
                                                                        ) : null}
                                                                        {params.InputProps.endAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            )}

                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </MainCard>
                </Grid>
                <Grid
                    item
                    xs={selectedContentPack ? 2 : 0}
                    sx={{
                        minWidth: 100,
                        flexShrink: 0,
                        mb: 3
                    }}
                >
                {selectedContentPack && (
                    <ButtonContainer>
                        <Button
                            variant="contained"
                            color="warning"
                            size="medium"
                            sx={{
                                width: "100%",
                                whiteSpace: "nowrap",
                                ...(lightMode
                                    ? {}
                                    : {
                                        '&.MuiButton-containedPrimary': {
                                            bgcolor: theme.palette.primary.main,
                                            color: getTextColor(theme.palette.primary.main),
                                            '&:hover': {
                                                bgcolor: "#69686b",
                                            },
                                        },
                                    }),
                            }}
                            onClick={() => handlePreview(selectedContentPack.internalId)}
                        >
                            Preview active
                        </Button>
                        </ButtonContainer>)}
                </Grid>
            </Grid> */}

            {/* Active Tab Content */}
            {activeTab === 0 && (
                <Box sx={{ width: '100%' }}>
                    <ActiveContentPackTable
                        data={assignments || []}
                        isLoading={assignmentsLoading || contentPacksLoading}
                        error={assignmentsError}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                        contentPacks={contentPacks || []}
                        groups={tenancyData?.groups || []}
                        onAssignConfirm={handleAssignConfirm}
                        onEdit={handleEditAssignment}
                        onDelete={handleDeleteAssignment}
                        isCreating={isCreatingAssignment}
                        isUpdating={isUpdatingAssignment}
                    />
                </Box>
            )}

            {/* Packs Tab Content */}
            {activeTab === 1 && (
                <>
                    {hasLegacyPacks && !legacyBannerDismissed && (
                        <Alert
                            severity="warning"
                            onClose={() => setLegacyBannerDismissed(true)}
                            sx={{ mb: 2, mt: 2 }}
                        >
                            You have {legacyPacks.length} legacy content pack{legacyPacks.length > 1 ? 's' : ''} from the old system.
                            Use the <strong>Convert</strong> button on each legacy pack to migrate it to the new assignment system.
                        </Alert>
                    )}
                    <Box sx={{ width: '100%' }}>
                        <ContentPackTable
                            contentPackData={filteredContentPacks}
                            isLoading={contentPacksLoading}
                            error={contentPacksError}
                            refetchContentPacks={refetchContentPacks}
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                            hideLegacyPacks={hideLegacyPacks}
                            setHideLegacyPacks={setHideLegacyPacks}
                            hasLegacyPacks={hasLegacyPacks}
                        />
                    </Box>
                </>
            )}
            <ModalProvider>
                <Modal
                    open={previewModalOpen}
                    onClose={handleClosePreviewModal}
                    aria-labelledby="simulation-preview-title"
                    aria-describedby="simulation-preview-description"
                    sx={{
                        '& .MuiBackdrop-root': {
                            backgroundColor: 'rgba(0, 0, 0, 0.5)'
                        }
                    }}
                >
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '8px',
                        width: '600px',
                        height: '90vh',
                        p: 2,
                        bgcolor: theme.palette.background.default,
                        boxShadow: 24,
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        visibility: coursePreviewOpen ? 'hidden' : 'visible'
                    }}>
                        {previewModalOpen && previewContentPackId && (
                            <ContentPackPreview
                                contentPackId={previewContentPackId}
                                setCoursePreviewOpen={setCoursePreviewOpen}
                            />
                        )}
                    </Box>
                </Modal>
            </ModalProvider>
        <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}>
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled"
                sx={{ width: "100%" }}>
                {snackbarMessage}
            </Alert>
            </Snackbar>
        <ConfirmationDialog
            open={dialogOpen}
            title={dialogTitle}
            content={dialogContent}
            onConfirm={() => {
                setDialogOpen(false);
                dialogAction?.();
            }}
            onCancel={() => {
                setDialogOpen(false);
                dialogCancelAction?.();
            }}
        />
        </>
    );
}

export default ContentPacks
